<template>
  <modal
    ref="editExamDataModal"
    :title="isSaving ? 'Salvando dados...' : 'Editar Dados Adicionais do Exame'"
    template="question"
    ref-name="editExamDataModal"
    first-button-text="Cancelar"
    :second-button-text="isSaving ? 'Salvando...' : 'Salvar'"
    :close-button-enabled="!isSaving"
    :icon-enabled="false"
    :first-button-disabled="isSaving"
    :second-button-disabled="
      isSaving || !hasAdditionalData || !hasChanges || hasEmptyFields
    "
    :hide-second-button="!hasAdditionalData"
    size="medium"
    class="edit-exam-data-modal"
    @first-cta="closeModal()"
    @second-cta="saveChanges()"
    @close="closeModal()"
  >
    <div
      v-if="isLoading"
      class="loading-container"
    >
      <spinner />
    </div>
    <div
      v-else-if="hasAdditionalData"
      class="additional-data-container"
    >
      <div class="exam-info mb-4">
        <h3 class="text-color-brand-primary">Exame: {{ selectedExam.codeExamBrand }}</h3>
        <p class="text-color-tundora">OS: {{ orderId }}</p>
      </div>

      <div
        v-for="(value, key) in additionalData"
        :key="key"
        class="form-field mb-3"
      >
        <field-container
          :label="getFieldLabel(key)"
          :error="isFieldEmpty(key)"
        >
          <v-date-picker
            v-if="getFieldType(key) === 'DATA'"
            v-model="editableData[key]"
            v-mask="'##/##/####'"
            :tint-color="'#7f1a54'"
            :pane-width="290"
            mode="single"
            style="width: 100%"
          >
            <div slot-scope="{ inputValue, updateValue }">
              <input
                :id="`field-${key}`"
                type="text"
                class="field-input"
                :value="inputValue"
                :placeholder="`Digite ${getFieldLabel(key).toLowerCase()}`"
                @change="updateValue($event.target.value)"
              />
            </div>
          </v-date-picker>

          <select
            v-else-if="getFieldType(key) === 'SELECT'"
            :id="`field-${key}`"
            v-model="editableData[key]"
            class="field-input field-select"
          >
            <option value="">Selecione</option>
            <option value="Sim">Sim</option>
            <option value="Não">Não</option>
          </select>

          <input
            v-else
            :id="`field-${key}`"
            v-model="editableData[key]"
            :type="getInputType(key)"
            class="field-input"
            :placeholder="`Digite ${getFieldLabel(key).toLowerCase()}`"
          />
        </field-container>
      </div>
    </div>
    <div
      v-else
      class="no-data-message"
    >
      <p>Este exame não possui dados adicionais para edição.</p>
    </div>
  </modal>
</template>

<script>
import Modal from '@/components/common/modals/Modal'
import Spinner from '@/components/common/Spinner'
import FieldContainer from '@/components/common/FieldContainer'
import notify from '@/api/notification'
import examApi from '@/api/exam'

export default {
  name: 'TraceabilityEditExamDataModal',

  components: {
    Modal,
    Spinner,
    FieldContainer,
  },

  props: {
    orderId: {
      type: [String, Number],
      default: null,
    },
    selectedExam: {
      type: Object,
      default: () => ({}),
    },
    idEntidade: {
      type: [String, Number],
      default: null,
    },
  },

  emits: ['updated', 'close'],

  data() {
    return {
      isClosing: false,
      isLoading: false,
      isSaving: false,
      additionalData: null,
      editableData: {},
      originalData: {},
      tipoResultado: {},
      nomeCampos: {},
    }
  },

  computed: {
    hasAdditionalData() {
      return (
        this.additionalData &&
        typeof this.additionalData === 'object' &&
        Object.keys(this.additionalData).length > 0
      )
    },
    hasChanges() {
      return Object.keys(this.originalData).some(
        key =>
          String(this.editableData[key] ?? '').trim() !==
          String(this.originalData[key] ?? '').trim()
      )
    },
    hasEmptyFields() {
      return Object.keys(this.editableData).some(key => {
        const value = this.editableData[key]
        return value === null || value === undefined || String(value).trim() === ''
      })
    },
  },

  methods: {
    async openModal() {
      this.$refs.editExamDataModal.open()

      if (this.selectedExam.preloadedData) {
        this.additionalData = this.selectedExam.preloadedData
        this.editableData = { ...this.selectedExam.preloadedData }
        this.originalData = { ...this.selectedExam.preloadedData }
        this.tipoResultado = this.selectedExam.tipoResultado || {}
        this.nomeCampos = this.selectedExam.nomeCampos || {}
      } else {
        this.additionalData = {}
      }
    },

    closeModal() {
      if (this.isClosing) return
      this.isClosing = true
      this.$refs.editExamDataModal.close()
      this.resetData()
      this.$emit('close')
      this.$nextTick(() => {
        this.isClosing = false
      })
    },

    resetData() {
      this.additionalData = null
      this.editableData = {}
      this.originalData = {}
      this.tipoResultado = {}
      this.nomeCampos = {}
      this.isLoading = false
      this.isSaving = false
    },

    async saveChanges() {
      this.isSaving = true

      const sanitizedData = {}
      Object.keys(this.editableData).forEach(key => {
        const value = this.editableData[key]
        sanitizedData[key] =
          this.getInputType(key) === 'text' ? String(value ?? '').trim() : value
      })

      try {
        await examApi.updateExamAdditionalData({
          orderId: this.orderId,
          laboratoryId: this.idEntidade,
          exames: [
            {
              codigoExame: this.selectedExam.codeExamBrand,
              dadosAdicionais: sanitizedData,
            },
          ],
        })

        this.$emit('updated', {
          orderId: this.orderId,
          examCode: this.selectedExam.codeExamBrand,
          data: sanitizedData,
        })

        this.closeModal()
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          'Não foi possível atualizar os dados adicionais.'

        notify({
          title: 'Erro',
          message: errorMessage,
        })
      } finally {
        this.isSaving = false
      }
    },

    formatFieldName(fieldName) {
      return fieldName
        .replace(/([A-Z])/g, ' $1')
        .replace(/_/g, ' ')
        .trim()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
    },

    getFieldLabel(key) {
      return this.nomeCampos[key] || this.formatFieldName(key)
    },

    getFieldType(key) {
      const fieldName = key
      const labelName = this.nomeCampos[key] || ''
      if (
        fieldName === 'DataUltimaMenstruacao' ||
        labelName === 'DataUltimaMenstruacao'
      ) {
        return 'DATA'
      }
      if (fieldName === 'FertilizacaoInVitro' || labelName === 'FertilizacaoInVitro') {
        return 'SELECT'
      }
      if (this.tipoResultado && this.tipoResultado[key] === 'N') {
        return 'NUMBER'
      }
      return 'TEXT'
    },

    getInputType(key) {
      if (this.tipoResultado && this.tipoResultado[key] === 'N') {
        return 'number'
      }
      return 'text'
    },

    isFieldEmpty(key) {
      const value = this.editableData[key]
      return value === null || value === undefined || String(value).trim() === ''
    },
  },
}
</script>

<style lang="scss" scoped>
.edit-exam-data-modal {
  ::v-deep .md-dialog {
    min-width: 800px;
    max-width: 1000px;
  }
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
}

.additional-data-container {
  .exam-info {
    border-bottom: 1px solid #e0e0e0;
    padding-bottom: 12px;
    margin-bottom: 20px;

    h3 {
      margin: 0 0 8px 0;
      font-size: 16px;
      font-weight: 600;
    }

    p {
      margin: 0;
      font-size: 14px;
    }
  }

  .form-field {
    .field-input {
      width: 100%;
      border: none;
      outline: none;
      padding: 8px 12px;
      font-size: 14px;
      font-family: 'Exo 2', sans-serif;
      background: transparent;

      &::placeholder {
        color: #999;
      }

      &:focus {
        outline: none;
      }
    }

    .field-select {
      cursor: pointer;
      appearance: auto;
    }
  }
}

.no-data-message {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 150px;
  text-align: center;

  p {
    color: #757575;
    font-size: 15px;
    margin: 0;
  }
}
</style>
